import { memo, useCallback, useEffect, useRef } from "react";

import { noop } from "lodash";
import { twJoin } from "tailwind-merge";

import { MatrixTablePredicate } from "src/clients/flow-api";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { ColumnContextMenu } from "src/matrixTableNode/ColumnContextMenu";
import { DormantCodeInput } from "src/matrixTableNode/DormantCodeInput";
import { useFloatingCodeInput } from "src/matrixTableNode/FloatingCodeInputProvider";
import { RowContextMenu } from "src/matrixTableNode/RowContextMenu";
import {
  useMatrixTableCellEditing,
  useMatrixTableCellHighlighted,
  useMatrixTableCellSelected,
  useMatrixTableColHovered,
  useMatrixTableError,
  useMatrixTableNavActions,
  useMatrixTableRowHovered,
} from "src/matrixTableNode/stores";
import { MatrixTableActions } from "src/matrixTableNode/utils";

const PredicateCellComponent = ({
  searchHighlightKey,
  predicateOperator,
  predicateValue,
  rowIndex,
  colIndex,
  actions,
  readOnly,
  rowId,
}: {
  rowId: string;
  predicateOperator: MatrixTablePredicate["operator"];
  predicateValue: MatrixTablePredicate["value"];
  rowIndex: number;
  colIndex: number;
  searchHighlightKey: string;
  actions: MatrixTableActions;
  readOnly: boolean;
}) => {
  const isColumnHovered = useMatrixTableColHovered(colIndex);
  const isRowHovered = useMatrixTableRowHovered(rowIndex);
  const eventRef = useRef<React.MouseEvent<HTMLDivElement> | null>(null);
  const { attach } = useFloatingCodeInput();
  const ref = useRef<HTMLDivElement>(null);
  const isSelected = useMatrixTableCellSelected({
    row: rowIndex,
    col: colIndex,
  });
  const isHighlighted = useMatrixTableCellHighlighted({
    row: rowIndex,
    col: colIndex,
  });
  const error = useMatrixTableError({ row: rowIndex, col: colIndex });
  const { editCell, hoverCell } = useMatrixTableNavActions();
  const isEditing = useMatrixTableCellEditing({
    row: rowIndex,
    col: colIndex,
  });
  const isSearchHighlighted = useIsFieldHighlighted(searchHighlightKey);
  const isHeader = rowIndex === -1;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      editCell({ row: rowIndex, col: colIndex });
      eventRef.current = e;
    },
    [rowIndex, colIndex, editCell],
  );

  useEffect(() => {
    if (isEditing) {
      attach({
        event: eventRef.current ?? undefined,
        ref,
        mode: "predicate",
        path: isHeader
          ? `column_predicates.${colIndex - 1}`
          : `row_predicates.${rowIndex}`,
      });
      eventRef.current = null;
    }
  }, [isEditing, attach, rowIndex, colIndex, ref, isHeader]);

  useEffect(() => {
    if (isSelected) {
      ref.current?.scrollIntoView({
        behavior: "instant",
        block: "nearest",
      });
    }
  }, [isSelected]);

  return (
    <div
      className={twJoin(
        "relative h-full w-full bg-white",
        isHeader ? "pt-3" : "pl-3",
        readOnly && "cursor-not-allowed bg-gray-100",
      )}
      data-loc={`matrix-table-node-predicate-cell-${rowIndex}-${colIndex}`}
      onMouseEnter={() =>
        !readOnly && hoverCell({ row: rowIndex, col: colIndex })
      }
    >
      {!readOnly &&
        (isHeader ? (
          <div className="absolute top-0 left-1/2 z-50">
            <ColumnContextMenu
              actions={actions}
              index={colIndex}
              show={isColumnHovered}
            />
          </div>
        ) : (
          <div className="absolute top-0.5 left-0 z-50">
            <RowContextMenu
              actions={actions}
              index={rowIndex}
              rowId={rowId}
              show={isRowHovered}
            />
          </div>
        ))}
      <div
        className={twJoin(
          "border-gray-100 border-r border-b",
          isHeader ? "border-t" : "border-l",
        )}
      >
        <DormantCodeInput
          ref={ref}
          dataLoc={`matrix-table-node-predicate-cell-${rowIndex}-${colIndex}`}
          error={error}
          isHighlighted={isHighlighted}
          isSearchHighlighted={isSearchHighlighted}
          isSelected={isSelected && !isEditing}
          operator={predicateOperator}
          readOnly={readOnly}
          value={predicateValue}
          variant="predicate"
          onClick={readOnly ? noop : handleClick}
        />
      </div>
    </div>
  );
};

export const PredicateCell = memo(PredicateCellComponent);
